/**
 * Dashboard API service
 * Provides functions to fetch dashboard metrics from the backend
 */

import { api } from './api';
import type { UnifiedBookingStatus } from '@leal-crm/shared';

/**
 * Recent booking structure returned by the dashboard API
 */
export interface RecentBooking {
  id: string;
  caseId: string;
  type: string;
  workflowStage?: string;
  status?: UnifiedBookingStatus;
  scheduledAt: number;
  confirmed: boolean;
  notes: string | null;
  bookingReference?: string | null;
  createdAt: number;
  updatedAt: number;
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
    year: number | null;
    color: string | null;
  };
}

/**
 * Dashboard metrics response structure
 */
export interface DashboardMetrics {
  todaysBookings: number;
  activeRepairs: number;
  pendingDelivery: number;
  totalCustomers: number;
  recentBookings: RecentBooking[];
  emailDeliveryRate?: number | null;
  approvalRate?: number | null;
  avgCycleTimeHours?: number | null;
}

export interface ActivityEvent {
  id: string;
  type: 'booking_created' | 'booking_event';
  eventType?: string;
  createdAt: number;
  booking: {
    id: string;
    caseId: string;
    reference: string | null;
    bookingType: string | null;
    customerName: string | null;
    vehicleRegistration: string | null;
  };
  statusInfo?: {
    code: string;
    name: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
  } | null;
  statusChange?: {
    from: string | null;
    to: string;
    fromInfo?: {
      code: string;
      name: string;
      color: string;
      bgColor: string | null;
      borderColor: string | null;
    } | null;
    toInfo?: {
      code: string;
      name: string;
      color: string;
      bgColor: string | null;
      borderColor: string | null;
    } | null;
  } | null;
  note?: string | null;
}

/**
 * Fetch dashboard metrics from the API
 */
export function getMetrics(): Promise<DashboardMetrics> {
  return api.get<DashboardMetrics>('/api/dashboard/metrics');
}

export function getActivity(limit = 25): Promise<{ items: ActivityEvent[] }> {
  return api.get<{ items: ActivityEvent[] }>(`/api/dashboard/activity?limit=${limit}`);
}

export const dashboardApi = {
  getMetrics,
  getActivity,
};
